import { ALERT_MANAGER_EVENT_TYPE_TOAST } from '../../utils/spds/constants/alert-manager';
import { SPDS_DEPRECATION_UNTIL_VERSION } from '../../utils/spds/constants/deprecations';
import {
  TOAST_DEFAULT_CTA_TIMEOUT,
  TOAST_DEFAULT_TIMEOUT,
  TOAST_ERROR_TYPE,
  TOAST_SUCCESS_TYPE,
  TOAST_SYSTEM_TYPE,
} from '../../utils/spds/constants/feedback';
import { deprecate } from '@ember/debug';
import Service, { service } from '@ember/service';

/**
 * Generates a user-friendly message for file upload notifications
 * @private
 * @param {number} countUploaded - The number of files uploaded
 * @param {File} file - The File object that was uploaded
 * @returns {string} A formatted message showing file count or filename
 */
function getFileUploadMessage(countUploaded, file) {
  return countUploaded > 1 ? `${countUploaded} files uploaded` : `"${file.name}" uploaded`;
}

/**
 * Emits a single deprecation warning for a "local toast" service method.
 * The toast container now renders all toasts in the browser top layer, so
 * a separate local container (and the matching `local*` API) is no longer
 * needed. The deprecated methods forward to their global counterparts so
 * existing call sites keep working until they migrate.
 */
function deprecateLocalToastMethod(method, replacement) {
  deprecate(
    `\`spds/toast#${method}\` is deprecated. Toasts now render in the browser top layer above modals and other layered UI, so a local toast variant is no longer required. Use \`spds/toast#${replacement}\` instead.`,
    false,
    {
      id: 'spds.toast.local-methods',
      for: '@simplepractice/spds',
      since: { available: '0.0.0', enabled: '0.0.0' },
      until: SPDS_DEPRECATION_UNTIL_VERSION,
    }
  );
}

/**
 * SPDS Toast Service
 *
 * Provides an API for displaying transient toast notifications. Toasts are
 * rendered as `popover="manual"` elements via the `Spds::Feedback::Toast::Container`
 * component, which lives in the browser top layer and stacks above modals
 * and other layered UI without needing a nested local container.
 *
 * The `local*` family of methods (`localError`, `localSuccess`, `localSystem`,
 * `fileUploadLocalToast`) is deprecated and now forwards to the matching
 * global method.
 *
 * @class SpdsToastService
 * @extends Service
 */
export default class SpdsToastService extends Service {
  @service alertManager;

  /**
   * Displays a global toast notification
   * @private
   * @param {object} data - The toast data object
   * @param {string} data.message - The message to display
   * @param {string} data.type - The toast type constant (ERROR, SUCCESS, or SYSTEM)
   * @param {number} data.timeout - Duration in milliseconds before auto-dismiss
   * @param {object} [data.cta] - Optional call-to-action configuration
   * @returns {Promise<void>}
   */
  #displayToast = async data => {
    await this.alertManager.broadcast(ALERT_MANAGER_EVENT_TYPE_TOAST, data);
  };

  /**
   * Determines the appropriate timeout value for a toast
   * @private
   * @param {object} [cta] - Optional call-to-action configuration
   * @param {number} [timeout] - Custom timeout duration in milliseconds
   * @returns {number} The timeout duration (uses TOAST_DEFAULT_CTA_TIMEOUT if cta exists, TOAST_DEFAULT_TIMEOUT otherwise)
   */
  #setTimeout = (cta, timeout) =>
    timeout ?? (cta ? TOAST_DEFAULT_CTA_TIMEOUT : TOAST_DEFAULT_TIMEOUT);

  /**
   * Displays a global error toast
   * @param {string} message - The error message to display
   * @param {object} [options] - Optional configuration
   * @param {object} [options.cta] - Optional call-to-action configuration
   * @param {string} options.cta.text - The CTA button label
   * @param {Function} options.cta.action - The CTA button click handler. Return a truthy value or undefined to auto-dismiss. Return false (or other falsy values) to keep toast open.
   * @param {Function} [options.onManualDismissal] - Callback when user manually dismisses the toast
   * @param {number} [options.timeout] - Duration in milliseconds before auto-dismiss
   *
   * @example
   * this.toast.error('Failed to save changes');
   *
   * @example
   * this.toast.error('Error occurred', { timeout: 10000 });
   */
  error = (message, { cta, onManualDismissal, timeout } = {}) => {
    this.#displayToast({
      cta,
      message,
      onManualDismissal,
      type: TOAST_ERROR_TYPE,
      timeout: this.#setTimeout(cta, timeout),
    });
  };

  /**
   * Displays a global success toast for file upload completion. Forwards to
   * `success` after emitting a deprecation warning.
   *
   * @deprecated Use {@link fileUploadToast} instead. The local toast variant
   * is no longer required because toasts render in the browser top layer.
   * @param {number} countUploaded - The number of files uploaded
   * @param {File} file - The File object that was uploaded
   */
  fileUploadLocalToast(countUploaded, file) {
    deprecateLocalToastMethod('fileUploadLocalToast', 'fileUploadToast');
    this.fileUploadToast(countUploaded, file);
  }

  /**
   * Displays a global success toast for file upload completion
   * Shows either the filename or count of multiple files uploaded.
   *
   * @param {number} countUploaded - The number of files uploaded
   * @param {File} file - The File object that was uploaded
   *
   * @example
   * this.toast.fileUploadToast(1, fileObject);
   * // Shows: "filename.pdf" uploaded
   */
  fileUploadToast(countUploaded, file) {
    this.success(getFileUploadMessage(countUploaded, file));
  }

  /**
   * Displays a global error toast. Forwards to {@link error} after emitting a
   * deprecation warning.
   *
   * @deprecated Use {@link error} instead. The local toast variant is no
   * longer required because toasts render in the browser top layer.
   */
  localError = (message, options = {}) => {
    deprecateLocalToastMethod('localError', 'error');
    this.error(message, options);
  };

  /**
   * Displays a global system toast. Forwards to {@link system} after emitting
   * a deprecation warning.
   *
   * @deprecated Use {@link system} instead. The local toast variant is no
   * longer required because toasts render in the browser top layer.
   */
  localSystem = (message, options = {}) => {
    deprecateLocalToastMethod('localSystem', 'system');
    this.system(message, options);
  };

  /**
   * Displays a global success toast. Forwards to {@link success} after
   * emitting a deprecation warning.
   *
   * @deprecated Use {@link success} instead. The local toast variant is no
   * longer required because toasts render in the browser top layer.
   */
  localSuccess = (message, options = {}) => {
    deprecateLocalToastMethod('localSuccess', 'success');
    this.success(message, options);
  };

  /**
   * Displays a global system toast
   * @param {string} message - The system message to display
   * @param {object} [options] - Optional configuration
   * @param {object} [options.cta] - Optional call-to-action configuration
   * @param {string} options.cta.text - The CTA button label
   * @param {Function} options.cta.action - The CTA button click handler. Return a truthy value or undefined to auto-dismiss. Return false (or other falsy values) to keep toast open.
   * @param {Function} [options.onManualDismissal] - Callback when user manually dismisses the toast
   * @param {number} [options.timeout] - Duration in milliseconds before auto-dismiss
   *
   * @example
   * this.toast.system('Scheduled maintenance in 10 minutes');
   */
  system = (message, { cta, onManualDismissal, timeout } = {}) => {
    this.#displayToast({
      cta,
      message,
      onManualDismissal,
      type: TOAST_SYSTEM_TYPE,
      timeout: this.#setTimeout(cta, timeout),
    });
  };

  /**
   * Displays a global success toast
   * @param {string} message - The success message to display
   * @param {object} [options] - Optional configuration
   * @param {object} [options.cta] - Optional call-to-action configuration
   * @param {string} options.cta.text - The CTA button label
   * @param {Function} options.cta.action - The CTA button click handler. Return a truthy value or undefined to auto-dismiss. Return false (or other falsy values) to keep toast open.
   * @param {Function} [options.onManualDismissal] - Callback when user manually dismisses the toast
   * @param {number} [options.timeout] - Duration in milliseconds before auto-dismiss
   *
   * @example
   * this.toast.success('Changes saved successfully');
   */
  success = (message, { cta, onManualDismissal, timeout } = {}) => {
    this.#displayToast({
      cta,
      message,
      onManualDismissal,
      type: TOAST_SUCCESS_TYPE,
      timeout: this.#setTimeout(cta, timeout),
    });
  };
}
